import React, { useCallback, useRef, useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  getColor,
  Icons,
  InputArea,
  Modal,
  Tooltip,
  Typography,
  useToast,
} from "fds";
import "./index.scss";
import { DocInfo, FileLinkPolicy, UploadFile } from "../../types/files";
import Dropzone, { FileRejection } from "react-dropzone";
import classNames from "classnames";
import utilsDocs from "../../shared/utils/docs";
import { getUserInfoFromLocalStorage } from "../../shared/utils/auth";
import { LoginDto } from "../../types/auth";
import useDocumentStore from "../../redux/dispatcher/useDocumentStore";
import { t } from "i18next";
import { nanoid } from "@reduxjs/toolkit";
interface ManaulRevisionCompleteModalProps {
  open: boolean;
  onClose: () => void;
  docInfo?: DocInfo;
}
const ManaulRevisionCompleteModal: React.FC<
  ManaulRevisionCompleteModalProps
> = ({ open, onClose, docInfo }) => {
  let dropzoneRef: any;
  const user = getUserInfoFromLocalStorage() as string | "";
  const loginDto: LoginDto = JSON.parse(user);
  const myName = loginDto?.userInfo?.userName ?? "";

  /** Hook 정리 */
  const toasts = useToast();
  const {
    doDocUploadRevisionFile,
    getDocUploadUidCreate,
    doDocUploadFileComplete,
    setUpdateUploadProgressFiles,
  } = useDocumentStore();
  /** State 정리 */
  const [uid, changeUid] = useState(""); // complete하기 위한 UID
  const [sendNotification, changeSendNotification] = useState(false); //알림 보낼지 checkbox
  const [uploadFile, changeUploadFile] = useState<File>(); // 수동 리비전 완료할 파일
  const [memo, changeMemo] = useState(""); // 알림 메시지
  const [progressPercent, setProgressPercent] = useState<number>(0);

  /** UseEffect 정리 */
  useEffect(() => {
    getDocUploadUidCreate().then((resp) => changeUid(resp.payload as string));
  }, []);

  /** 함수 정리 */
  /** 수동 리비전 할 문서를 드롭하거나 업로드했을 때 */
  const onDrop = async (
    acceptedFiles: File[],
    fileRejections: FileRejection[],
    e: any
  ): Promise<void> => {
    if (!docInfo) return;
    e.preventDefault();
    e.stopPropagation();
    if (uid) {
      const resp: any = await doDocUploadRevisionFile(
        uid + "",
        acceptedFiles[0],
        docInfo?.syncId,
        setProgressPercent
      );
      if (!resp.payload) {
        if (
          !["NOT_MATCH_SYNCID", "INVALID_REVISION_NO"].includes(
            resp.error.message
          )
        )
          toasts.toastMsg(nanoid(), resp.error.message, "error");
        else {
          toasts.toastMsg(nanoid(), t("file.alert.invalidRevVer"), "error");
        }
      } else {
        changeUploadFile(acceptedFiles[0]);
      }
    }
  };
  /** 수동 리비전 완료 */
  const handleUploadManualDoc = async () => {
    if (!docInfo) return;
    const resp = await doDocUploadFileComplete(uid + "", {
      memo: memo,
      syncId: docInfo.syncId,
      sendNotification: sendNotification,
      workgroupId: docInfo?.workgroup.workgroupId,
    });
    if (resp.payload) {
      toasts.toastMsg(
        nanoid(),
        t("file.manualRev.alert.completeSuccess"),
        "success"
      );
    } else {
      toasts.toastMsg(nanoid(), t("file.alert.invalidRevVer"), "error");
    }
    setUpdateUploadProgressFiles({} as UploadFile);
    onClose();
  };
  return (
    <Modal
      title={t("file.action.manualRevComplete")}
      open={open}
      onClose={() => {
        setUpdateUploadProgressFiles({} as UploadFile);
        onClose();
      }}
      width={540}
      classes={"manualUpload"}
      footer={
        uploadFile ? (
          <Box>
            <Button
              btnStyle="fill"
              label={t("common.checkin")}
              onClick={handleUploadManualDoc}
            />
          </Box>
        ) : (
          <Box width="100%">
            <Button
              btnStyle="link"
              label={t("common.upload")}
              style={{ marginLeft: 0 }}
              leadingIcon={<Icons variant="line" label="upload" />}
              onClick={(e) => {
                e.preventDefault();
                dropzoneRef?.open();
              }}
            />
          </Box>
        )
      }
      body={
        <Box width="100%">
          {uploadFile ? (
            <Box direction="column" width="100%">
              <Box classes="manualUpload-uploadInfo">
                {utilsDocs.renderDocIcon(uploadFile.name, 40)}
                <Box
                  direction="column"
                  classes="mg ml-12"
                  width="calc(100% - 52px)"
                >
                  <Tooltip message={uploadFile.name}>
                    <Typography type="body2_rg" ellipsis>
                      {uploadFile.name}
                    </Typography>
                  </Tooltip>
                  <Box style={{ marginTop: "5px" }}>
                    <Typography
                      type="caption1_rg"
                      exactColor={getColor("COLOR_TEXT_SECONDARY")}
                    >
                      {utilsDocs.fileSizeFormat(uploadFile.size) + " • "}
                    </Typography>
                    <Tooltip message={myName} maxWidth={"calc(100% - 180px)"}>
                      <Typography
                        type="caption1_rg"
                        exactColor={getColor("COLOR_TEXT_SECONDARY")}
                        ellipsis
                        classes="mg ml-2 mr-2"
                      >
                        {myName}
                      </Typography>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
              <Box classes="mg mt-32 ">
                <Checkbox
                  check={sendNotification}
                  onClick={() => changeSendNotification(!sendNotification)}
                />
                <Typography type="body2_rg" classes="mg ml-8">
                  {t("file.manualRev.alarmAuthDesc")}
                </Typography>
              </Box>
              <Box classes="mg mt-8" direction="column">
                <Typography classes="mg mt-12" type="subtitle2">
                  {t("common.message")}
                </Typography>
                <InputArea
                  placeholder={t("common.messagePlaceholder")}
                  classes="mg mt-6"
                  rows={3}
                  disabled={!sendNotification}
                  onChange={(e) => changeMemo(e.target.value)}
                  value={memo}
                  maxLength={500}
                  style={{ marginBottom: "4px" }}
                  size="sm"
                />
              </Box>
            </Box>
          ) : (
            <Dropzone
              noClick
              noKeyboard
              ref={(node) => {
                if (node) dropzoneRef = node;
              }}
              onDrop={onDrop}
            >
              {({ getRootProps, getInputProps, isDragActive }) => (
                <div
                  {...getRootProps({ classNames: "dropzone" })}
                  className={classNames("dropzone", {
                    accept: isDragActive,
                    false: !isDragActive,
                  })}
                >
                  <input {...getInputProps()} />
                  <Box
                    classes={classNames("dropzone-dragging", {
                      accept: isDragActive,
                    })}
                    style={{ display: isDragActive ? "flex" : "none" }}
                  >
                    <img
                      src={require("../../shared/images/pages/File/UploadFile.png")}
                      alt={"UploadFile"}
                      height={110}
                    />
                    <Typography
                      type="h3"
                      textAlign="center"
                      exactColor={getColor("COLOR_BG_PRIMARY")}
                      classes="mg mt-20"
                    >
                      {t("file.dropHere")}
                    </Typography>
                  </Box>
                  <Box classes="dropzone-info">
                    <img
                      src={require("../../shared/images/pages/File/UploadFile.png")}
                      alt={"UploadFile"}
                      width={200}
                      height={100}
                    />
                    <Typography type="body2_rg" width={256} textAlign="center">
                      {t("file.uploadInstruction")}
                    </Typography>
                  </Box>
                </div>
              )}
            </Dropzone>
          )}
        </Box>
      }
    />
  );
};
export default ManaulRevisionCompleteModal;
